import type { EditorError } from 'mini-code'
import { DX7 } from './dx7.ts'
import { getAllFunctionNames } from './function-definitions.ts'
import { Lexer } from './lexer.ts'
import { PRELUDE } from './lib/synths.ts'

const preludeSources: string[] = [DX7, PRELUDE]
const prelude = `${preludeSources.join('\n')}\n`
export const preludeLineCount = prelude.split('\n').length - 1

export const adjustErrorLineNumbers = (message: string): string => {
  return message.replace(/at line (\d+)(?:, column (\d+))?/g, (match, lineStr, colStr) => {
    const errorLine = parseInt(lineStr, 10)
    const normalizedLine = Math.max(1, errorLine - preludeLineCount)
    if (colStr) {
      return `at line ${normalizedLine}, column ${colStr}`
    }
    return `at line ${normalizedLine}`
  })
}

const lineColumnToPosition = (value: string, line: number, column: number): number => {
  const lines = value.split('\n')
  let pos = 0
  for (let i = 0; i < line - 1 && i < lines.length; i++) {
    pos += lines[i]!.length + 1
  }
  pos += column - 1
  return Math.min(pos, value.length)
}

const positionToLineColumn = (
  value: string,
  position: number,
): { line: number; column: number } => {
  const before = value.substring(0, position)
  const line = before.split('\n').length
  const lastNewline = before.lastIndexOf('\n')
  const column = lastNewline === -1 ? before.length + 1 : position - lastNewline
  return { line, column }
}

const removeLineColumnFromMessage = (message: string): string => {
  return message
    .replace(/\s*\(?at line \d+(?:, column \d+)?\)?/gi, '')
    .replace(/\s*\(?line \d+(?:, column \d+)?\)?/gi, '')
    .trim()
}

const addLineColumnToMessage = (message: string, line: number, column: number): string => {
  if (line <= 0 || column <= 0) {
    return message
  }
  const cleanedMessage = removeLineColumnFromMessage(message)
  if (!cleanedMessage) {
    return `Error (at line ${line}, column ${column})`
  }
  return `${cleanedMessage} (at line ${line}, column ${column})`
}

export const convertToEditorErrors = (error: Error, value: string): EditorError[] => {
  const initialFrom = 0
  const initialTo = value.length
  let from = initialFrom
  let to = initialTo
  let message = error.message
  let foundLine: number | null = null
  let foundColumn: number | null = null

  // Handle runtime errors (sample loading, IR loading, etc.) that aren't compilation errors
  // These should be displayed as-is without trying to parse them as code errors
  const errorMsgLower = error.message.toLowerCase()
  if (
    error.message.includes('Failed to load sample')
    || error.message.includes('Failed to fetch sample')
    || error.message.includes('Failed to fetch freesound sample')
    || errorMsgLower.includes('freesound')
    || error.message.includes('Failed to send sample')
    || error.message.includes('Failed to load impulse response')
    || error.message.includes('Failed to fetch impulse response')
  ) {
    // Special handling for freesound errors - try to find and highlight the ID
    if (errorMsgLower.includes('freesound')) {
      const freesoundIdMatch = error.message.match(/freesound sample "(\d+)"/i)
      if (freesoundIdMatch) {
        const sampleId = freesoundIdMatch[1]!
        try {
          const lexer = new Lexer(value)
          const tokens = lexer.tokenize()

          // Find freesound function calls and locate the ID
          for (let i = 0; i < tokens.length; i++) {
            const token = tokens[i]!
            if (token.type === 'IDENTIFIER' && token.value === 'freesound') {
              // Look for the next LPAREN
              const nextToken = tokens[i + 1]
              if (nextToken?.type === 'LPAREN') {
                // Check if next token is the ID (positional) or if we have named args
                let foundId = false
                let idStartPos = -1
                let idEndPos = -1

                // Look for id:number or just number as first argument
                // Skip whitespace and commas
                let firstArgIndex = i + 2
                while (
                  firstArgIndex < tokens.length
                  && (tokens[firstArgIndex]?.type === 'NEWLINE'
                    || tokens[firstArgIndex]?.type === 'COMMA')
                ) {
                  firstArgIndex++
                }

                // Track parenthesis depth to handle nested calls like beat(1)
                let parenDepth = 1 // We're already inside the freesound( call
                for (let j = firstArgIndex; j < tokens.length; j++) {
                  const t = tokens[j]!
                  if (t.type === 'LPAREN') {
                    parenDepth++
                  }
                  else if (t.type === 'RPAREN') {
                    parenDepth--
                    if (parenDepth === 0) break // Closed the freesound call
                  }
                  if (t.type === 'NEWLINE' || t.type === 'COMMA') continue

                  // Check for named argument id:number
                  if (t.type === 'IDENTIFIER' && t.value === 'id') {
                    let k = j + 1
                    // Skip whitespace/newlines
                    while (k < tokens.length && tokens[k]?.type === 'NEWLINE') k++
                    const colonToken = tokens[k]
                    if (colonToken?.type === 'COLON') {
                      k++
                      // Skip whitespace/newlines after colon
                      while (k < tokens.length && tokens[k]?.type === 'NEWLINE') k++
                      const numToken = tokens[k]
                      if (numToken && numToken.type === 'NUMBER') {
                        // Compare as strings - both should be string representations of the number
                        if (
                          numToken.value === sampleId
                          || String(parseInt(numToken.value, 10)) === sampleId
                        ) {
                          idStartPos = lineColumnToPosition(value, numToken.line, numToken.column)
                          idEndPos = idStartPos + numToken.value.length
                          foundId = true
                          break
                        }
                      }
                    }
                  }

                  // Check for positional number argument (first argument after opening paren)
                  if (j === firstArgIndex && t.type === 'NUMBER' && t.value === sampleId) {
                    idStartPos = lineColumnToPosition(value, t.line, t.column)
                    idEndPos = idStartPos + sampleId.length
                    foundId = true
                    break
                  }
                }

                if (foundId && idStartPos >= 0) {
                  const lc = positionToLineColumn(value, idStartPos)
                  const endLc = positionToLineColumn(value, idEndPos)
                  return [
                    {
                      line: lc.line - 1,
                      startColumn: lc.column - 1,
                      endColumn: endLc.column - 1,
                      message: adjustErrorLineNumbers(message),
                    },
                  ]
                }
              }
            }
          }
        }
        catch {
          // Fall through to default error handling
        }
      }
    }

    return [
      {
        line: 0,
        startColumn: 0,
        endColumn: Math.min(10, value.length),
        message: adjustErrorLineNumbers(message),
      },
    ]
  }

  const lineColumnMatch = error.message.match(/at line (\d+)(?:, column (\d+))?/)
  if (lineColumnMatch) {
    const errorLine = parseInt(lineColumnMatch[1]!, 10)
    const normalizedLine = Math.max(1, errorLine - preludeLineCount)
    const errorColumn = lineColumnMatch[2] ? parseInt(lineColumnMatch[2]!, 10) : 1

    from = lineColumnToPosition(value, normalizedLine, errorColumn)

    // Handle pick() errors with line/column info
    const pickRequiresArgMatch = error.message.match(/pick\(\) requires an element selector argument/)
    const subdivideRequiresArgMatch = error.message.match(
      /subdivide\(\) requires (?:a beat argument|a pattern array|a callback function)/,
    )
    if (pickRequiresArgMatch) {
      try {
        const lexer = new Lexer(value)
        const tokens = lexer.tokenize()

        // Find the pick() call at or near the target position
        for (let i = 0; i < tokens.length; i++) {
          const token = tokens[i]!
          if (token.type === 'IDENTIFIER' && token.value === 'pick') {
            const tokenPos = lineColumnToPosition(value, token.line, token.column)
            const nextToken = tokens[i + 1]
            // Check if this is a pick() call and if it's at or near the target position
            if (nextToken?.type === 'LPAREN' && tokenPos <= from && from < tokenPos + 'pick'.length + 10) {
              from = tokenPos
              to = tokenPos + 'pick'.length
              foundLine = token.line
              foundColumn = token.column
              break
            }
          }
        }

        // If we didn't find an exact match, use the position with pick length
        if (to === initialTo) {
          to = from + 'pick'.length
        }
      }
      catch {
        // Fallback to pick length
        to = from + 'pick'.length
      }
    }
    // Handle subdivide() errors with line/column info
    else if (subdivideRequiresArgMatch) {
      try {
        const lexer = new Lexer(value)
        const tokens = lexer.tokenize()

        // Find the subdivide() call at or near the target position
        for (let i = 0; i < tokens.length; i++) {
          const token = tokens[i]!
          if (token.type === 'IDENTIFIER' && token.value === 'subdivide') {
            const tokenPos = lineColumnToPosition(value, token.line, token.column)
            const nextToken = tokens[i + 1]
            // Check if this is a subdivide() call and if it's at or near the target position
            if (nextToken?.type === 'LPAREN' && tokenPos <= from && from < tokenPos + 'subdivide'.length + 10) {
              from = tokenPos
              to = tokenPos + 'subdivide'.length
              foundLine = token.line
              foundColumn = token.column
              break
            }
          }
        }

        // If we didn't find an exact match, use the position with subdivide length
        if (to === initialTo) {
          to = from + 'subdivide'.length
        }
      }
      catch {
        // Fallback to subdivide length
        to = from + 'subdivide'.length
      }
    }
    // Handle invalid property errors with line/column info
    const invalidPropertyMatch = error.message.match(/Invalid property '(\w+)'/)
    if (invalidPropertyMatch) {
      const propertyName = invalidPropertyMatch[1]!
      try {
        const lexer = new Lexer(value)
        const tokens = lexer.tokenize()

        // Find the property access at the specific line/column location
        // Look for DOT followed by IDENTIFIER matching the property name
        for (let i = 0; i < tokens.length; i++) {
          const token = tokens[i]!
          if (token.type === 'DOT') {
            const nextToken = tokens[i + 1]
            if (nextToken?.type === 'IDENTIFIER' && nextToken.value === propertyName) {
              const dotPos = lineColumnToPosition(value, token.line, token.column)
              const propPos = lineColumnToPosition(value, nextToken.line, nextToken.column)
              // Check if the property token position matches the error position
              // Allow some tolerance for position matching
              if (propPos <= from && from <= propPos + propertyName.length + 2) {
                from = dotPos
                to = propPos + propertyName.length
                foundLine = token.line
                foundColumn = token.column
                break
              }
            }
          }
        }

        // If we didn't find an exact match, use the property name length at the position
        if (to === initialTo) {
          to = from + 1 + propertyName.length // 1 for the dot
        }
      }
      catch {
        // Fallback to property name length
        to = from + 1 + propertyName.length
      }
    }
    // Handle undefined variable errors with line/column info
    if (!pickRequiresArgMatch && !subdivideRequiresArgMatch && !invalidPropertyMatch) {
      const undefinedVarMatch = error.message.match(/Undefined variable:\s+(\w+)/)
      if (undefinedVarMatch) {
        const varName = undefinedVarMatch[1]!
        try {
          const lexer = new Lexer(value)
          const tokens = lexer.tokenize()

          // Find the variable at the specific line/column location
          for (let i = 0; i < tokens.length; i++) {
            const token = tokens[i]!
            if (token.type === 'IDENTIFIER' && token.value === varName) {
              const tokenPos = lineColumnToPosition(value, token.line, token.column)
              // Check if this token is at or near the target position
              if (tokenPos <= from && from < tokenPos + varName.length) {
                from = tokenPos
                to = tokenPos + varName.length
                foundLine = token.line
                foundColumn = token.column
                break
              }
            }
          }

          // If we didn't find an exact match, use the variable name length at the position
          if (to === initialTo) {
            to = from + varName.length
          }
        }
        catch {
          // Fallback to variable name length
          to = from + varName.length
        }
      }
    }

    if (!pickRequiresArgMatch && !subdivideRequiresArgMatch && !invalidPropertyMatch) {
      // Check for mini-notation pattern errors with invalid notes
      const invalidMiniNotationMatch = error.message.match(
        /Invalid mini-notation pattern "([^"]+)"[^:]*:\s*Invalid note syntax "([^"]+)"/,
      )
      if (invalidMiniNotationMatch) {
        const pattern = invalidMiniNotationMatch[1]!
        const invalidNote = invalidMiniNotationMatch[2]!

        try {
          const lexer = new Lexer(value)
          const tokens = lexer.tokenize()

          for (let i = 0; i < tokens.length; i++) {
            const token = tokens[i]!
            if (token.type === 'STRING') {
              // First, try to find the invalid note directly in the token value
              const noteIndex = token.value.indexOf(invalidNote)
              if (noteIndex >= 0) {
                const tokenPos = lineColumnToPosition(value, token.line, token.column)
                // token.column points to the opening quote, so add 1 to skip it, then add noteIndex
                from = tokenPos + 1 + noteIndex
                to = from + invalidNote.length
                message =
                  `Invalid note syntax "${invalidNote}". Notes must be in the format: letter (a-g) + optional accidental (#/b) + octave number (e.g., "c3", "d#4", "eb2").`
                foundLine = token.line
                foundColumn = token.column + 1 + noteIndex
                break
              }

              // If direct search fails, check if the pattern appears in this string token
              const patternIndex = token.value.indexOf(pattern)
              if (patternIndex >= 0) {
                // Find the invalid note within the pattern
                const noteIndexInPattern = pattern.indexOf(invalidNote)
                if (noteIndexInPattern >= 0) {
                  const tokenPos = lineColumnToPosition(value, token.line, token.column)
                  // token.column points to the opening quote, so add 1 to skip it, then add patternIndex, then noteIndexInPattern
                  from = tokenPos + 1 + patternIndex + noteIndexInPattern
                  to = from + invalidNote.length
                  message =
                    `Invalid note syntax "${invalidNote}". Notes must be in the format: letter (a-g) + optional accidental (#/b) + octave number (e.g., "c3", "d#4", "eb2").`
                  const notePos = positionToLineColumn(value, from)
                  foundLine = notePos.line
                  foundColumn = notePos.column
                  break
                }
              }
            }
          }
        }
        catch {
          // Fallback: use the line/column position with note length
          to = from + invalidNote.length
        }
      }
      else if (error.message.includes('Unterminated string')) {
        const lineStart = lineColumnToPosition(value, normalizedLine, 1)
        const lines = value.split('\n')
        const line = lines[normalizedLine - 1]
        if (line) {
          const lineEnd = lineStart + line.length
          to = Math.min(from + 20, lineEnd)
        }
        else {
          to = from + 20
        }
      }
      else if (error.message.includes('got ')) {
        const gotMatch = error.message.match(/got '([^']+)'/)
        if (gotMatch) {
          const tokenValue = gotMatch[1]!
          to = from + tokenValue.length
        }
        else {
          to = from + 1
        }
      }
      else if (to === initialTo) {
        to = from + 1
      }
    }

    const lc = foundLine !== null && foundColumn !== null
      ? { line: foundLine, column: foundColumn }
      : positionToLineColumn(value, from)
    const endLc = positionToLineColumn(value, to)

    return [
      {
        line: lc.line - 1,
        startColumn: lc.column - 1,
        endColumn: endLc.column - 1,
        message: addLineColumnToMessage(message, lc.line, lc.column),
      },
    ]
  }

  const knownFunctions = new Set(getAllFunctionNames())

  try {
    const lexer = new Lexer(value)
    const tokens = lexer.tokenize()

    // Check for unknown function error FIRST, before other heuristics
    // This ensures we highlight the function mentioned in the error message
    const unknownFuncErrorMatch = error.message.match(/Unknown function:\s*(\w+)/)
    const unknownBlockTypeMatch = error.message.match(/Unknown block type for counter:\s*(\w+)/)
    const errorMatch = unknownFuncErrorMatch || unknownBlockTypeMatch
    if (errorMatch) {
      const funcName = errorMatch[1]!
      // Find the function call using tokens to get accurate line/column
      // Check both with and without parentheses (for pipeline stages)
      for (let i = 0; i < tokens.length; i++) {
        const token = tokens[i]!
        if (token.type === 'IDENTIFIER' && token.value === funcName) {
          const nextToken = tokens[i + 1]
          // Match function calls with parentheses OR identifiers in pipeline stages
          if (nextToken?.type === 'LPAREN'
            || (i > 0 && tokens[i - 1]?.type === 'PIPE'))
          {
            const pos = lineColumnToPosition(value, token.line, token.column)
            from = pos
            to = pos + funcName.length
            // Calculate line/column from position to ensure accuracy
            const calculated = positionToLineColumn(value, pos)
            foundLine = calculated.line
            foundColumn = calculated.column
            message = `Unknown function: ${funcName}`
            break
          }
        }
      }

      // Fallback to regex if not found in tokens
      if (from === initialFrom && to === initialTo) {
        // Try with parentheses first
        const reWithParen = new RegExp(`\\b${funcName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*\\(`, 'g')
        const matchWithParen = reWithParen.exec(value)
        if (matchWithParen) {
          from = matchWithParen.index
          to = from + funcName.length
          message = `Unknown function: ${funcName}`
        }
        else {
          // Try without parentheses (for pipeline stages)
          const reWithoutParen = new RegExp(`\\|>\\s*${funcName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'g')
          const matchWithoutParen = reWithoutParen.exec(value)
          if (matchWithoutParen) {
            from = matchWithoutParen.index + matchWithoutParen[0].indexOf(funcName)
            to = from + funcName.length
            message = `Unknown function: ${funcName}`
          }
        }
      }
    }

    const undefinedVarMatch = error.message.match(/Undefined variable:\s+(\w+)/)
    const sequenceNotFoundMatch = error.message.match(/Sequence '(\w+)' not found/)
    const unknownParamMatch = error.message.match(
      /Unknown parameter '(\w+)' for function '(\w+)\(\)'/,
    )
    const cannotUseStringMatch = error.message.match(
      /Cannot use string "([^"]+)" where a number is expected/,
    )
    const invalidMiniNotationMatch = error.message.match(
      /Invalid mini-notation pattern "([^"]+)"[^:]*:\s*Invalid note syntax "([^"]+)"/,
    )
    const invalidNoteSyntaxMatch = error.message.match(
      /Invalid note syntax in "([^"]+)". Note "([^"]+)" is not valid/,
    )
    const invalidNoteSyntaxSimpleMatch = error.message.match(
      /Invalid note syntax "([^"]+)"/,
    )
    const invalidDurationFormatMatch = error.message.match(
      /Invalid duration (?:format|expression) "([^"]+)" in note: ([^\s]+)/,
    )
    const invalidVelocityMatch = error.message.match(
      /Invalid velocity value "([^"]+)" in note: ([^\s]+)/,
    )
    const invalidPitchFormatMatch = error.message.match(
      /Invalid pitch format "([^"]+)" in note: ([^\s]+)/,
    )
    const invalidHoldMatch = error.message.match(
      /Invalid hold value(?: "([^"]+)")? in note: ([^\s]+)/,
    )
    const invalidTokenInSequenceMatch = error.message.match(/Invalid token "([^"]+)" in sequence/)
    const invalidNoteFormatMatch = error.message.match(/Invalid note format: ([^\s]+)/)
    const mustBeCalledWithParensMatch = error.message.match(
      /Function '(\w+)' must be called with parentheses: \w+\(\.\.\.\)/,
    )

    if (from === 0 && to === value.length && mustBeCalledWithParensMatch) {
      const funcName = mustBeCalledWithParensMatch[1]!
      for (let i = 0; i < tokens.length; i++) {
        const token = tokens[i]!
        if (token.type === 'IDENTIFIER' && token.value === funcName) {
          const nextToken = tokens[i + 1]
          if (nextToken?.type !== 'LPAREN') {
            const pos = lineColumnToPosition(value, token.line, token.column)
            from = pos
            to = pos + funcName.length
            message = `Function '${funcName}' must be called with parentheses: ${funcName}(...)`
            break
          }
        }
      }
    }

    const pickRequiresArgMatch = error.message.match(/pick\(\) requires an element selector argument/)
    if (from === 0 && to === value.length && pickRequiresArgMatch) {
      // Find .pick( or pick( calls
      for (let i = 0; i < tokens.length; i++) {
        const token = tokens[i]!
        if (token.type === 'IDENTIFIER' && token.value === 'pick') {
          const prevToken = tokens[i - 1]
          const nextToken = tokens[i + 1]
          // Match both .pick( and pick( patterns
          if ((nextToken && nextToken.type === 'LPAREN')
            || (prevToken?.type === 'DOT' && nextToken && nextToken.type === 'LPAREN'))
          {
            const pos = lineColumnToPosition(value, token.line, token.column)
            from = pos
            to = pos + 'pick'.length
            message = 'pick() requires an element selector argument'
            break
          }
        }
      }
    }

    const subdivideRequiresArgMatch = error.message.match(
      /subdivide\(\) requires (?:a beat argument|a pattern array|a callback function)/,
    )
    if (from === 0 && to === value.length && subdivideRequiresArgMatch) {
      // Find .subdivide( or subdivide( calls
      for (let i = 0; i < tokens.length; i++) {
        const token = tokens[i]!
        if (token.type === 'IDENTIFIER' && token.value === 'subdivide') {
          const prevToken = tokens[i - 1]
          const nextToken = tokens[i + 1]
          // Match both .subdivide( and subdivide( patterns
          if ((nextToken && nextToken.type === 'LPAREN')
            || (prevToken?.type === 'DOT' && nextToken && nextToken.type === 'LPAREN'))
          {
            const pos = lineColumnToPosition(value, token.line, token.column)
            from = pos
            to = pos + 'subdivide'.length
            if (error.message.match(/subdivide\(\) requires a beat argument/)) {
              message =
                'subdivide() requires a beat argument: [values].subdivide(beat:1/4, pattern:[1,2,3]) or [values].subdivide(1/4, [1,2,3])'
            }
            else if (error.message.match(/subdivide\(\) requires a pattern array/)) {
              message =
                'subdivide() requires a pattern array: [values].subdivide(beat:1/4, pattern:[1,2,3]) or [values].subdivide(1/4, [1,2,3])'
            }
            else if (error.message.match(/subdivide\(\) requires a callback function/)) {
              message =
                'subdivide() requires a callback function: [values].subdivide(beat:1/4, pattern:[1,2,3], (trig,value)->...) or [values].subdivide(1/4, [1,2,3], (trig,value)->...)'
            }
            else {
              message = error.message
            }
            break
          }
        }
      }
    }

    if (undefinedVarMatch) {
      const varName = undefinedVarMatch[1]!

      // First, check if the error message includes line/column information
      const varLineColumnMatch = error.message.match(/at line (\d+)(?:, column (\d+))?/)
      if (varLineColumnMatch) {
        const errorLine = parseInt(varLineColumnMatch[1]!, 10)
        const normalizedLine = Math.max(1, errorLine - preludeLineCount)
        const errorColumn = varLineColumnMatch[2] ? parseInt(varLineColumnMatch[2]!, 10) : 1

        // Find the variable at the specific line/column location
        const targetPos = lineColumnToPosition(value, normalizedLine, errorColumn)

        // Verify that the variable name matches at this location
        for (let i = 0; i < tokens.length; i++) {
          const token = tokens[i]!
          if (token.type === 'IDENTIFIER' && token.value === varName) {
            const tokenPos = lineColumnToPosition(value, token.line, token.column)
            // Check if this token is at or near the target position
            if (tokenPos <= targetPos && targetPos < tokenPos + varName.length) {
              from = tokenPos
              to = tokenPos + varName.length
              message = `Undefined variable: ${varName}`
              foundLine = token.line
              foundColumn = token.column
              break
            }
          }
        }

        // If we found a match using line/column, we're done
        if (from !== initialFrom || to !== initialTo) {
          // Already set above
        }
        else {
          // Fallback: use the line/column position directly
          from = targetPos
          to = targetPos + varName.length
          message = `Undefined variable: ${varName}`
        }
      }
      else {
        // No line/column info in error message - search through tokens
        // Prefer usage over assignment/definition
        let usageMatch: { from: number; to: number; line: number; column: number } | null = null
        let fallbackMatch: { from: number; to: number; line: number; column: number } | null = null

        for (let i = 0; i < tokens.length; i++) {
          const token = tokens[i]!
          if (token.type === 'IDENTIFIER' && token.value === varName) {
            const nextToken = tokens[i + 1]
            const isAssignment = nextToken?.type === 'ASSIGN'
            const isFunctionDef = nextToken?.type === 'ARROW'

            const pos = lineColumnToPosition(value, token.line, token.column)
            const match = { from: pos, to: pos + varName.length, line: token.line, column: token.column }

            if (!isAssignment && !isFunctionDef) {
              usageMatch = match
            }
            else if (!fallbackMatch) {
              fallbackMatch = match
            }
          }
        }

        const bestMatch = usageMatch || fallbackMatch
        if (bestMatch) {
          from = bestMatch.from
          to = bestMatch.to
          foundLine = bestMatch.line
          foundColumn = bestMatch.column
          message = `Undefined variable: ${varName}`
        }
      }
    }

    if (from === 0 && to === value.length && sequenceNotFoundMatch) {
      const seqName = sequenceNotFoundMatch[1]!
      const re = new RegExp(`\\b${seqName}\\b`, 'g')
      const match = re.exec(value)
      if (match) {
        from = match.index
        to = from + seqName.length
        message = `Sequence '${seqName}' not found`
      }
    }

    if (from === 0 && to === value.length && unknownParamMatch) {
      const paramName = unknownParamMatch[1]!
      const funcName = unknownParamMatch[2]!
      const re = new RegExp(`${funcName}\\s*\\([^)]*\\b${paramName}\\b`, 'g')
      const funcMatch = re.exec(value)
      if (funcMatch) {
        const funcCallText = funcMatch[0]
        const paramIndex = funcCallText.indexOf(paramName)
        from = funcMatch.index + paramIndex
        to = from + paramName.length
        message = `Unknown parameter '${paramName}' for function '${funcName}()'`
      }
    }

    if (from === 0 && to === value.length && invalidMiniNotationMatch) {
      const pattern = invalidMiniNotationMatch[1]!
      const invalidNote = invalidMiniNotationMatch[2]!

      for (let i = 0; i < tokens.length; i++) {
        const token = tokens[i]!
        if (token.type === 'STRING') {
          // First, try to find the invalid note directly in the token value
          const noteIndex = token.value.indexOf(invalidNote)
          if (noteIndex >= 0) {
            const tokenPos = lineColumnToPosition(value, token.line, token.column)
            // token.column points to the opening quote, so add 1 to skip it, then add noteIndex
            from = tokenPos + 1 + noteIndex
            to = from + invalidNote.length
            message =
              `Invalid note syntax "${invalidNote}". Notes must be in the format: letter (a-g) + optional accidental (#/b) + octave number (e.g., "c3", "d#4", "eb2").`
            break
          }

          // If direct search fails, check if the pattern appears in this string token
          const patternIndex = token.value.indexOf(pattern)
          if (patternIndex >= 0) {
            // Find the invalid note within the pattern
            const noteIndexInPattern = pattern.indexOf(invalidNote)
            if (noteIndexInPattern >= 0) {
              const tokenPos = lineColumnToPosition(value, token.line, token.column)
              // token.column points to the opening quote, so add 1 to skip it, then add patternIndex, then noteIndexInPattern
              from = tokenPos + 1 + patternIndex + noteIndexInPattern
              to = from + invalidNote.length
              message =
                `Invalid note syntax "${invalidNote}". Notes must be in the format: letter (a-g) + optional accidental (#/b) + octave number (e.g., "c3", "d#4", "eb2").`
              break
            }
          }
        }
      }

      if (from === 0 && to === value.length) {
        // Fallback: try to find the invalid note directly in the source
        const escapedNote = invalidNote.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        const re = new RegExp(escapedNote, 'g')
        const match = re.exec(value)
        if (match) {
          from = match.index
          to = from + invalidNote.length
          message =
            `Invalid note syntax "${invalidNote}". Notes must be in the format: letter (a-g) + optional accidental (#/b) + octave number (e.g., "c3", "d#4", "eb2").`
        }
      }
    }

    if (from === 0 && to === value.length && invalidNoteSyntaxMatch) {
      const fullString = invalidNoteSyntaxMatch[1]!
      const invalidNote = invalidNoteSyntaxMatch[2]!

      for (let i = 0; i < tokens.length; i++) {
        const token = tokens[i]!
        if (token.type === 'STRING' && token.value === fullString) {
          const pos = lineColumnToPosition(value, token.line, token.column)
          const noteStart = token.value.indexOf(invalidNote)
          if (noteStart >= 0) {
            from = pos + 1 + noteStart
            to = from + invalidNote.length
          }
          else {
            from = pos
            to = pos + token.value.length + 2
          }
          message =
            `Invalid note syntax. Note "${invalidNote}" is not valid. Notes must be in the format: letter (a-g) + optional accidental (#/b) + octave number (e.g., "c3", "d#4", "eb2").`
          break
        }
      }

      if (from === 0 && to === value.length) {
        const escapedNote = invalidNote.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        const re = new RegExp(escapedNote, 'g')
        const match = re.exec(value)
        if (match) {
          from = match.index
          to = from + invalidNote.length
          message =
            `Invalid note syntax. Note "${invalidNote}" is not valid. Notes must be in the format: letter (a-g) + optional accidental (#/b) + octave number (e.g., "c3", "d#4", "eb2").`
        }
      }
    }

    if (from === 0 && to === value.length && invalidNoteSyntaxSimpleMatch) {
      const noteValue = invalidNoteSyntaxSimpleMatch[1]!

      for (let i = 0; i < tokens.length; i++) {
        const token = tokens[i]!
        if (token.type === 'STRING') {
          // Check if the note value appears in this string token
          const noteIndex = token.value.indexOf(noteValue)
          if (noteIndex >= 0) {
            const tokenPos = lineColumnToPosition(value, token.line, token.column)
            // token.column points to the opening quote, so add 1 to skip it, then add noteIndex
            from = tokenPos + 1 + noteIndex
            to = from + noteValue.length
            message =
              `Invalid note syntax "${noteValue}". Notes must be in the format: letter (a-g) + optional accidental (#/b) + octave number (e.g., "c3", "d#4", "eb2").`
            break
          }
        }
      }

      if (from === 0 && to === value.length) {
        const escapedValue = noteValue.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        // Try to find the note value within quoted strings or as a standalone value
        const re = new RegExp(`(["'])([^"']*${escapedValue}[^"']*)\\1|\\b${escapedValue}\\b`, 'g')
        const match = re.exec(value)
        if (match) {
          if (match[1]) {
            // Found in quotes - find the note value within the quoted content
            const quotedContent = match[2]!
            const noteIndex = quotedContent.indexOf(noteValue)
            if (noteIndex >= 0) {
              from = match.index! + 1 + noteIndex // +1 to skip opening quote
              to = from + noteValue.length
            }
            else {
              from = match.index!
              to = from + match[0].length
            }
          }
          else {
            // Found as standalone
            from = match.index!
            to = from + noteValue.length
          }
          message =
            `Invalid note syntax "${noteValue}". Notes must be in the format: letter (a-g) + optional accidental (#/b) + octave number (e.g., "c3", "d#4", "eb2").`
        }
      }
    }

    if (from === 0 && to === value.length && invalidDurationFormatMatch) {
      const invalidDuration = invalidDurationFormatMatch[1]!
      const fullNote = invalidDurationFormatMatch[2]!

      for (let i = 0; i < tokens.length; i++) {
        const token = tokens[i]!
        if (token.type === 'STRING') {
          const noteMatch = token.value.match(
            new RegExp(`([^\\s]+):${invalidDuration.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}@`),
          )
          if (noteMatch) {
            const pos = lineColumnToPosition(value, token.line, token.column)
            const noteStart = token.value.indexOf(noteMatch[1]!)
            const durationStart = token.value.indexOf(invalidDuration, noteStart)
            if (durationStart >= 0) {
              from = pos + 1 + durationStart
              to = from + invalidDuration.length
              message =
                `Invalid duration format "${invalidDuration}". Duration must be a number or fraction (e.g., "1/4", "7/8", "1/8+1/16").`
              break
            }
          }
        }
      }

      if (from === 0 && to === value.length) {
        const escapedDuration = invalidDuration.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        const re = new RegExp(escapedDuration, 'g')
        const match = re.exec(value)
        if (match) {
          from = match.index
          to = from + invalidDuration.length
          message =
            `Invalid duration format "${invalidDuration}". Duration must be a number or fraction (e.g., "1/4", "7/8", "1/8+1/16").`
        }
      }
    }

    if (from === 0 && to === value.length && invalidPitchFormatMatch) {
      const invalidPitch = invalidPitchFormatMatch[1]!
      const fullNote = invalidPitchFormatMatch[2]!

      for (let i = 0; i < tokens.length; i++) {
        const token = tokens[i]!
        if (token.type === 'STRING') {
          const noteMatch = token.value.match(
            new RegExp(`${invalidPitch.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}:`),
          )
          if (noteMatch) {
            const pos = lineColumnToPosition(value, token.line, token.column)
            const pitchStart = token.value.indexOf(invalidPitch)
            if (pitchStart >= 0) {
              from = pos + 1 + pitchStart
              to = from + invalidPitch.length
              if (error.message.includes('Underscore (_) can only be used alone')) {
                message =
                  `Invalid pitch format "${invalidPitch}". Underscore (_) can only be used alone for rests, not combined with other characters.`
              }
              else {
                message =
                  `Invalid pitch format "${invalidPitch}". Pitch must be valid notes (e.g., "c4", "d#3", "eb2") or "_" for a rest.`
              }
              break
            }
          }
        }
      }

      if (from === 0 && to === value.length) {
        const escapedPitch = invalidPitch.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        const re = new RegExp(`${escapedPitch}:`, 'g')
        const match = re.exec(value)
        if (match) {
          from = match.index
          to = from + invalidPitch.length
          if (error.message.includes('Underscore (_) can only be used alone')) {
            message =
              `Invalid pitch format "${invalidPitch}". Underscore (_) can only be used alone for rests, not combined with other characters.`
          }
          else {
            message =
              `Invalid pitch format "${invalidPitch}". Pitch must be valid notes (e.g., "c4", "d#3", "eb2") or "_" for a rest.`
          }
        }
      }
    }

    if (from === 0 && to === value.length && invalidHoldMatch) {
      const invalidHold = invalidHoldMatch[1] || ''
      const fullNote = invalidHoldMatch[2]!

      if (invalidHold) {
        // Invalid hold value like "a" - find ,a pattern
        for (let i = 0; i < tokens.length; i++) {
          const token = tokens[i]!
          if (token.type === 'STRING') {
            const holdPattern = ',' + invalidHold
            const holdIndex = token.value.indexOf(holdPattern)
            if (holdIndex >= 0) {
              const pos = lineColumnToPosition(value, token.line, token.column)
              from = pos + 1 + holdIndex + 1
              to = from + invalidHold.length
              message = `Invalid hold value "${invalidHold}". Hold must be a number.`
              break
            }
          }
        }

        if (from === 0 && to === value.length) {
          const escapedHold = invalidHold.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
          const re = new RegExp(`,` + escapedHold + `(?:\\s|$|['"])`, 'g')
          const match = re.exec(value)
          if (match) {
            from = match.index + 1
            to = from + invalidHold.length
            message = `Invalid hold value "${invalidHold}". Hold must be a number.`
          }
        }
      }
      else {
        // Empty hold value - find comma at end of velocity or followed by space/quote
        // First try to find it in tokens
        for (let i = 0; i < tokens.length; i++) {
          const token = tokens[i]!
          if (token.type === 'STRING') {
            // Search for @velocity, pattern where comma is at end or followed by space
            let searchStart = 0
            while (true) {
              const atIndex = token.value.indexOf('@', searchStart)
              if (atIndex < 0) break

              const afterAt = token.value.slice(atIndex + 1)
              const commaIndex = afterAt.indexOf(',')
              if (commaIndex >= 0) {
                const velocityPart = afterAt.slice(0, commaIndex).trim()
                const afterComma = afterAt.slice(commaIndex + 1).trim()

                // Check if comma is at end or followed only by whitespace
                if (afterComma === '' || afterComma.match(/^\s*$/)) {
                  const pos = lineColumnToPosition(value, token.line, token.column)
                  const commaPosInToken = atIndex + 1 + commaIndex
                  from = pos + 1 + commaPosInToken
                  to = from + 1
                  message =
                    `Invalid hold value. Hold value cannot be empty. If you don't need a hold value, omit the comma.`
                  break
                }
              }
              searchStart = atIndex + 1
            }
            if (from !== 0 || to !== value.length) break
          }
        }

        // Fallback: search in full value
        if (from === 0 && to === value.length) {
          const re = /@[^@\s,]+,/g
          let match: RegExpExecArray | null = null

          while ((match = re.exec(value))) {
            const commaIndex = match.index + match[0].length - 1
            const afterComma = value.slice(commaIndex + 1).trim()

            // Check if comma is at end or followed only by whitespace/quote
            if (afterComma === '' || afterComma.match(/^['"]?$/)) {
              from = commaIndex
              to = from + 1
              message =
                `Invalid hold value. Hold value cannot be empty. If you don't need a hold value, omit the comma.`
              break
            }
          }
        }
      }
    }

    if (from === 0 && to === value.length && invalidNoteFormatMatch) {
      const invalidNote = invalidNoteFormatMatch[1]!

      for (let i = 0; i < tokens.length; i++) {
        const token = tokens[i]!
        if (token.type === 'STRING') {
          const noteIndex = token.value.indexOf(invalidNote)
          if (noteIndex >= 0) {
            const pos = lineColumnToPosition(value, token.line, token.column)
            from = pos + 1 + noteIndex
            to = from + invalidNote.length
            message =
              `Invalid note format: ${invalidNote}. Notes must be in the format: pitch:duration@velocity,hold (e.g., "c4:1/4@1" or "_:1/8@0,.5" where hold is optional).`
            break
          }
        }
      }

      if (from === 0 && to === value.length) {
        const escapedNote = invalidNote.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        const re = new RegExp(escapedNote, 'g')
        const match = re.exec(value)
        if (match) {
          from = match.index
          to = from + invalidNote.length
          message =
            `Invalid note format: ${invalidNote}. Notes must be in the format: pitch:duration@velocity,hold (e.g., "c4:1/4@1" or "_:1/8@0,.5" where hold is optional).`
        }
      }
    }

    if (from === 0 && to === value.length && invalidTokenInSequenceMatch) {
      const invalidToken = invalidTokenInSequenceMatch[1]!

      for (let i = 0; i < tokens.length; i++) {
        const token = tokens[i]!
        if (token.type === 'STRING') {
          const tokenIndex = token.value.indexOf(invalidToken)
          if (tokenIndex >= 0) {
            const pos = lineColumnToPosition(value, token.line, token.column)
            from = pos + 1 + tokenIndex
            to = from + invalidToken.length
            message =
              `Invalid token "${invalidToken}" in sequence. All tokens must be valid notes in the format: note:duration@velocity,hold (e.g., "c4:1/4@1" or "_:1/8@0,.5" where hold is optional).`
            break
          }
        }
      }

      if (from === 0 && to === value.length) {
        const escapedToken = invalidToken.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        const re = new RegExp(`\\b${escapedToken}\\b`, 'g')
        const match = re.exec(value)
        if (match) {
          from = match.index
          to = from + invalidToken.length
          message =
            `Invalid token "${invalidToken}" in sequence. All tokens must be valid notes in the format: note:duration@velocity,hold (e.g., "c4:1/4@1" or "_:1/8@0,.5" where hold is optional).`
        }
      }
    }

    if (from === 0 && to === value.length && invalidVelocityMatch) {
      const invalidVelocity = invalidVelocityMatch[1]!
      const fullNote = invalidVelocityMatch[2]!

      for (let i = 0; i < tokens.length; i++) {
        const token = tokens[i]!
        if (token.type === 'STRING') {
          const noteMatch = token.value.match(
            new RegExp(`@${invalidVelocity.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?:\\s|$)`),
          )
          if (noteMatch) {
            const pos = lineColumnToPosition(value, token.line, token.column)
            const velocityStart = token.value.indexOf('@' + invalidVelocity)
            if (velocityStart >= 0) {
              from = pos + 1 + velocityStart + 1
              to = from + invalidVelocity.length
              message = `Invalid velocity value "${invalidVelocity}". Velocity must be a number (0-1) or "_".`
              break
            }
          }
        }
      }

      if (from === 0 && to === value.length) {
        const escapedVelocity = invalidVelocity.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        const re = new RegExp(`@${escapedVelocity}(?:\\s|$)`, 'g')
        const match = re.exec(value)
        if (match) {
          from = match.index + 1
          to = from + invalidVelocity.length
          message = `Invalid velocity value "${invalidVelocity}". Velocity must be a number (0-1) or "_".`
        }
      }
    }

    if (from === 0 && to === value.length && cannotUseStringMatch) {
      const stringValue = cannotUseStringMatch[1]!

      for (let i = 0; i < tokens.length; i++) {
        const token = tokens[i]!
        if (token.type === 'STRING' && token.value === stringValue) {
          const pos = lineColumnToPosition(value, token.line, token.column)
          from = pos
          to = pos + stringValue.length + 2
          message =
            `Cannot use string "${stringValue}" where a number is expected. If you meant to use a note, include an octave number (e.g., "${stringValue}4").`
          break
        }
      }

      if (from === 0 && to === value.length) {
        const escapedValue = stringValue.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        const re = new RegExp(`"${escapedValue}"|'${escapedValue}'|${escapedValue}`, 'g')
        const match = re.exec(value)
        if (match) {
          from = match.index
          to = from + match[0].length
          message =
            `Cannot use string "${stringValue}" where a number is expected. If you meant to use a note, include an octave number (e.g., "${stringValue}4").`
        }
      }
    }

    // Skip all heuristic error detection - only show errors that were actually thrown

    const hasLocation = from !== initialFrom || to !== initialTo

    let finalMessage: string
    if (hasLocation) {
      const lc = foundLine !== null && foundColumn !== null
        ? { line: foundLine, column: foundColumn }
        : positionToLineColumn(value, from)
      const endLc = positionToLineColumn(value, to)
      finalMessage = addLineColumnToMessage(message, lc.line, lc.column)

      return [
        {
          line: lc.line - 1,
          startColumn: lc.column - 1,
          endColumn: endLc.column - 1,
          message: finalMessage,
        },
      ]
    }
    else {
      // Fallback: show error exactly as thrown
      finalMessage = message
      const lc = positionToLineColumn(value, 0)

      return [
        {
          line: 0,
          startColumn: 0,
          endColumn: Math.min(10, value.length),
          message: finalMessage,
        },
      ]
    }
  }
  catch {
    // Fallback: show error exactly as thrown
    const lc = positionToLineColumn(value, 0)
    return [
      {
        line: 0,
        startColumn: 0,
        endColumn: Math.min(10, value.length),
        message: message,
      },
    ]
  }
}
